import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useLocation } from 'react-router-dom';
import { ulid } from 'ulid';

import {
	Alert,
	AlertDescription,
	AlertTitle
} from '@/components/ui/alert';
import { FormPageLayout } from '@/components/layout/form-page-layout';
import { usePageHeader } from '@/contexts/PageHeaderContext';
import { useGo } from '@/hooks/useResourceNavigation';
import { useUnsavedChanges } from '@/hooks/useUnsavedChanges';

import { UserForm, type UserFormValues, type RoleOption } from './user-form';

interface CloneData {
	firstName?: string;
	lastName?: string;
	email?: string;
	username?: string;
	isActive?: boolean;
	roleIds?: string[];
}

interface LocationState {
	cloneData?: CloneData;
}

const defaultRoleOptions: RoleOption[] = [];

export const UserCreate = () => {
	const { setPageHeader, resetPageHeader } = usePageHeader();
	const go = useGo();
	const location = useLocation();
	const locationState = location.state as LocationState | null;
	const cloneData = locationState?.cloneData;

	const form = useForm<UserFormValues>({
		defaultValues: {
			firstName: cloneData?.firstName ?? '',
			lastName: cloneData?.lastName ?? '',
			email: cloneData?.email ?? '',
			username: cloneData?.username ?? '',
			isActive: cloneData?.isActive ?? true,
			password: '',
			confirmPassword: '',
			roleIds: cloneData?.roleIds ?? []
		}
	});

	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submissionNotice, setSubmissionNotice] = useState<string | null>(
		null
	);

	useUnsavedChanges(form.formState.isDirty, {
		isSubmitSuccessful: form.formState.isSubmitSuccessful,
		isSubmitting
	});

	useEffect(() => {
		setPageHeader({
			title: cloneData ? 'Clone user' : 'Create user',
			description: cloneData
				? 'Create a new user based on an existing one'
				: 'Add a new user to the system',
			showBackButton: true,
			onBackClick: () => go({ to: '/users' })
		});

		return () => {
			resetPageHeader();
		};
	}, [cloneData, go, resetPageHeader, setPageHeader]);

	// Generate idempotent user ID for creation
	const idempotentUserId = useMemo(() => ulid(), []);

	const roleOptions = useMemo(() => defaultRoleOptions, []);

	const handleSubmit = async (values: UserFormValues) => {
		setSubmissionNotice(null);
		setIsSubmitting(true);

		try {
			// The GraphQL API currently exposes read operations for users only.
			// Persisting changes will require a dedicated mutation.
			// Until then, log the payload for integration testing.
			// eslint-disable-next-line no-console
			console.info('Captured user payload', {
				userId: idempotentUserId,
				...values
			});

			setSubmissionNotice(
				'User creation is not yet connected to the GraphQL API. The captured values have been logged for integration testing.'
			);

			// TODO: When user creation mutation is implemented, navigate with selection state:
			// go({
			//   to: '/users',
			//   options: {
			//     state: { selectUserId: idempotentUserId }
			//   }
			// });
		} finally {
			setIsSubmitting(false);
		}
	};

	return (
		<FormPageLayout>
			{submissionNotice && (
				<Alert>
					<AlertTitle>Action required</AlertTitle>
					<AlertDescription>{submissionNotice}</AlertDescription>
				</Alert>
			)}

			<UserForm
				mode="create"
				form={form}
				roleOptions={roleOptions}
				isSubmitting={isSubmitting}
				onSubmit={handleSubmit}
				headerTitle={cloneData ? 'Clone user' : 'Create user'}
			/>
		</FormPageLayout>
	);
};
